import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Users, Clock, Bell, Trash2, Armchair, MapPin } from 'lucide-react';
import { buildWhatsappLink, WHATSAPP_ICON_URL } from '../../../../../utils/whatsapp';
import {
  WaitlistEntry,
  fetchWaitlistEntries,
  readyWaitlistEntry as readyWaitlistEntryApi,
  cancelWaitlistEntry as cancelWaitlistEntryApi,
} from '../../services/api.ts';

interface WaitlistPanelProps {
  dateKey: string;
  selectedDateLabel: string;
  onCountChange?: (count: number) => void;
  onSeatEntry?: (entry: WaitlistEntry) => void;
  refreshKey?: number;
}

const getEntryName = (entry: WaitlistEntry): string => String(entry?.nome_cliente || '').trim();
const getEntryPhone = (entry: WaitlistEntry): string => String(entry?.telefone_cliente || '').trim();
const getEntryNotes = (entry: WaitlistEntry): string => String(entry?.obs || '').trim();

const getTimestamp = (value: unknown, fallback = Date.now()): number => {
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatWaitDuration = (createdAt: number, now: number): string => {
  const minutes = Math.max(0, Math.floor((now - createdAt) / 60000));
  if (minutes < 1) return 'agora mesmo';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `há ${hours}h${String(rest).padStart(2, '0')}` : `há ${hours}h`;
};

const formatClock = (timestamp: number): string => {
  try {
    return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch (error) {
    return '--:--';
  }
};

const WaitlistPanel: React.FC<WaitlistPanelProps> = ({
  dateKey,
  selectedDateLabel,
  onCountChange,
  onSeatEntry,
  refreshKey,
}) => {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState<number>(() => Date.now());
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const firstLoadDoneRef = useRef(false);
  const lastLoadDateRef = useRef<string | null>(null);
  const confirmResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadEntries = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setNow(Date.now());
    try {
      const data = await fetchWaitlistEntries({ data: dateKey, ativas: true });
      setEntries(Array.isArray(data) ? data : []);
      setError('');
    } catch (loadError) {
      setEntries([]);
      setError('Não foi possível carregar a fila de espera.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateKey]);

  useEffect(() => {
    const sameDate = lastLoadDateRef.current === dateKey;
    const silent = firstLoadDoneRef.current && sameDate;
    firstLoadDoneRef.current = true;
    lastLoadDateRef.current = dateKey;
    loadEntries(silent);
  }, [dateKey, loadEntries, refreshKey]);

  useEffect(() => () => {
    if (confirmResetTimer.current) clearTimeout(confirmResetTimer.current);
  }, []);

  const orderedEntries = useMemo(
    () => [...entries].sort((a, b) => {
      const aCalled = a.status === 'ready';
      const bCalled = b.status === 'ready';
      if (aCalled !== bCalled) return aCalled ? -1 : 1;
      if (aCalled && bCalled) return getTimestamp(b.ready_at, 0) - getTimestamp(a.ready_at, 0);
      return getTimestamp(a.created_at) - getTimestamp(b.created_at);
    }),
    [entries]
  );

  useEffect(() => {
    onCountChange?.(orderedEntries.length);
  }, [orderedEntries.length, onCountChange]);

  const updateEntry = useCallback((updatedEntry: WaitlistEntry) => {
    setEntries((current) => current.map((item) => (
      String(item.id) === String(updatedEntry.id) ? updatedEntry : item
    )));
  }, []);

  const handleReady = useCallback(async (entry: WaitlistEntry) => {
    const entryId = String(entry.id);
    setActionLoadingId(`ready:${entryId}`);
    try {
      const updatedEntry = await readyWaitlistEntryApi(entryId);
      updateEntry(updatedEntry);
      toast.info(updatedEntry.whatsapp_opt_in
        ? `${getEntryName(updatedEntry)} chamado(a). O aviso será enviado pelo Reservo.`
        : `${getEntryName(updatedEntry)} chamado(a).`);
    } catch (readyError) {
      toast.error('Não foi possível chamar esta pessoa.');
    } finally {
      setActionLoadingId(null);
    }
  }, [updateEntry]);

  const handleRemoveClick = useCallback(async (entryId: string) => {
    if (confirmResetTimer.current) clearTimeout(confirmResetTimer.current);

    if (confirmingRemoveId !== entryId) {
      setConfirmingRemoveId(entryId);
      confirmResetTimer.current = setTimeout(() => setConfirmingRemoveId(null), 3500);
      return;
    }

    setActionLoadingId(`remove:${entryId}`);
    try {
      await cancelWaitlistEntryApi(entryId);
      setEntries((current) => current.filter((item) => String(item.id) !== entryId));
      setConfirmingRemoveId(null);
      toast.info('Removido da fila de espera.');
    } catch (removeError) {
      toast.error('Não foi possível remover esta pessoa da fila.');
    } finally {
      setActionLoadingId(null);
    }
  }, [confirmingRemoveId]);

  return (
    <div className="reservo-waitlist">
      <div className="reservo-waitlist-header">
        <div className="reservo-waitlist-heading">
          <h3>Fila de Espera</h3>
          <p className="reservo-waitlist-description">
            Pessoas aguardando mesa em {selectedDateLabel}. Chame por ordem de chegada.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="reservo-waitlist-empty">
          <div className="reservo-waitlist-empty-icon">
            <Clock size={26} />
          </div>
          <strong>Carregando fila de espera</strong>
          <span>Aguarde enquanto buscamos as entradas do dia.</span>
        </div>
      ) : error ? (
        <div className="reservo-waitlist-empty">
          <div className="reservo-waitlist-empty-icon">
            <Clock size={26} />
          </div>
          <strong>{error}</strong>
          <span>Atualize a tela ou tente novamente em instantes.</span>
        </div>
      ) : orderedEntries.length === 0 ? (
        <div className="reservo-waitlist-empty">
          <div className="reservo-waitlist-empty-icon">
            <Clock size={26} />
          </div>
          <strong>Ninguém na fila de espera</strong>
          <span>As pessoas entram sozinhas pela página pública da fila. Compartilhe o link para começar.</span>
        </div>
      ) : (
        <ol className="reservo-waitlist-list">
          {orderedEntries.map((entry, index) => {
            const entryId = String(entry.id);
            const name = getEntryName(entry);
            const phone = getEntryPhone(entry);
            const whatsappLink = buildWhatsappLink(phone);
            const notes = getEntryNotes(entry);
            const createdAt = getTimestamp(entry.created_at);
            const position = Number(entry.position || 0) > 0 ? Number(entry.position) : index + 1;
            const isReady = entry.status === 'ready';
            const waitMinutes = Math.floor((now - createdAt) / 60000);
            const isOverdue = waitMinutes >= 30;
            const isConfirmingRemove = confirmingRemoveId === entryId;
            const readyLoading = actionLoadingId === `ready:${entryId}`;
            const removeLoading = actionLoadingId === `remove:${entryId}`;
            const anyActionLoading = Boolean(actionLoadingId);

            return (
              <li key={entry.id} className={`reservo-waitlist-item ${isReady ? 'is-ready' : ''}`}>
                <div className="reservo-waitlist-main">
                  <div className="reservo-waitlist-position">
                    <span className="reservo-waitlist-position-index">{position}</span>
                    <span className="reservo-waitlist-position-label">{isReady ? 'chamado' : 'na fila'}</span>
                  </div>
                  <div className="reservo-waitlist-info">
                    <div className="reservo-waitlist-name-row">
                      <span className="reservo-waitlist-name">{name}</span>
                      {isReady && (
                        <span className="reservo-waitlist-tag is-ready">
                          <Bell size={12} />
                          Esperando cliente
                        </span>
                      )}
                    </div>
                    <div className="reservo-waitlist-meta">
                      <span className="reservo-waitlist-pill is-party">
                        <Users size={14} />
                        {entry.numero_pessoas} pessoa{entry.numero_pessoas > 1 ? 's' : ''}
                      </span>
                      <span
                        className={`reservo-waitlist-pill is-wait ${isOverdue ? 'is-overdue' : ''}`}
                        title={`Entrou às ${formatClock(createdAt)}`}
                      >
                        <Clock size={14} />
                        Esperando {formatWaitDuration(createdAt, now)}
                      </span>
                      {entry.sala_nome ? (
                        <span className="reservo-waitlist-pill is-room">
                          <MapPin size={14} />
                          {entry.sala_nome}
                        </span>
                      ) : null}
                      {whatsappLink ? (
                        <a
                          className="reservo-waitlist-pill is-phone is-whatsapp-link"
                          href={whatsappLink}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          title={`Enviar WhatsApp para ${phone}`}
                        >
                          <img src={WHATSAPP_ICON_URL} alt="" aria-hidden="true" className="reservo-whatsapp-pill-icon" />
                          {phone}
                        </a>
                      ) : phone ? (
                        <span className="reservo-waitlist-pill is-phone">{phone}</span>
                      ) : null}
                      {entry.whatsapp_opt_in ? (
                        <span className="reservo-waitlist-pill is-whatsapp">
                          <Bell size={14} />
                          WhatsApp ativo
                        </span>
                      ) : null}
                    </div>
                    {notes ? (
                      <div className="reservo-waitlist-observation" title={notes}>
                        <span className="reservo-waitlist-observation-label">Obs:</span>
                        <span className="reservo-waitlist-observation-text">{notes}</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="reservo-waitlist-actions">
                  {!isReady && (
                    <button
                      type="button"
                      className="room-today-action is-call"
                      onClick={() => handleReady(entry)}
                      disabled={anyActionLoading}
                    >
                      <Bell size={15} />
                      {readyLoading ? 'Chamando...' : 'Chamar'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="room-today-action"
                    onClick={() => onSeatEntry?.(entry)}
                    disabled={anyActionLoading}
                  >
                    <Armchair size={15} />
                    Sentar
                  </button>
                  <button
                    type="button"
                    className={`room-today-action ${isConfirmingRemove ? 'is-danger' : 'is-warning'}`}
                    onClick={() => handleRemoveClick(entryId)}
                    disabled={anyActionLoading && !removeLoading}
                  >
                    <Trash2 size={15} />
                    {removeLoading ? 'Removendo...' : (isConfirmingRemove ? 'Confirmar?' : 'Remover')}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default WaitlistPanel;
